import { client } from './client';

export interface AppointmentDetails {
  id: string;
  patientName: string;
  providerName: string;
  appointmentType: string;
  scheduledTime: string;
  scheduledDate: string;
  status: 'pending' | 'confirmed' | 'declined';
}

export interface ProviderConfirmationResponse {
  success: boolean;
  message?: string;
  error?: string;
  appointment?: AppointmentDetails;
}

export interface ProviderConfirmationError {
  error: string;
  message?: string;
}

export const fetchAppointmentDetails = async (confirmationToken: string): Promise<AppointmentDetails> => {
  try {
    const response = await client.get(`/provider-confirmations/details/${confirmationToken}`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching appointment details:', error);
    throw error;
  }
};

export const acceptAppointment = async (confirmationToken: string): Promise<ProviderConfirmationResponse> => {
  try {
    const response = await client.post(`/provider-confirmations/accept/${confirmationToken}`);
    return response.data;
  } catch (error: any) {
    console.error('Error accepting appointment:', error);
    throw error;
  }
};

export const declineAppointment = async (confirmationToken: string): Promise<ProviderConfirmationResponse> => {
  try {
    const response = await client.post(`/provider-confirmations/decline/${confirmationToken}`);
    return response.data;
  } catch (error: any) {
    console.error('Error declining appointment:', error);
    throw error;
  }
};

export const errorMessages = {
  INVALID_TOKEN: 'This confirmation link is invalid or has expired.',
  ALREADY_CONFIRMED: 'Appointment already confirmed',
  ALREADY_DECLINED: 'Appointment already declined',
  CANNOT_DECLINE_CONFIRMED: 'Cannot decline an already confirmed appointment',
  CANNOT_CONFIRM_DECLINED: 'Cannot confirm an already declined appointment',
  NETWORK_ERROR: 'Please check your connection and try again.',
  GENERIC_ERROR: 'Something went wrong. Please try again later.',
} as const;

export const getErrorMessage = (error: any): string => {
  if (error?.response?.data?.kind) {
    const errorKind = error.response.data.kind;

    switch (errorKind) {
      case 'ERR_PROVIDER_CONFIRMATION_NOT_FOUND':
        return errorMessages.INVALID_TOKEN;
      case 'ERR_PROVIDER_CONFIRMATION_CANNOT_DECLINE_CONFIRMED':
        return errorMessages.CANNOT_DECLINE_CONFIRMED;
      case 'ERR_PROVIDER_CONFIRMATION_CANNOT_CONFIRM_DECLINED':
        return errorMessages.CANNOT_CONFIRM_DECLINED;
      default:
        return errorMessages.GENERIC_ERROR;
    }
  }

  if (error?.response?.data?.error) {
    const errorCode = error.response.data.error;

    switch (errorCode) {
      case '[11703-0]':
        return errorMessages.INVALID_TOKEN;
      case '[11703-1]':
        return errorMessages.ALREADY_CONFIRMED;
      case '[11703-2]':
        return errorMessages.ALREADY_DECLINED;
      case '[11703-3]':
        return errorMessages.CANNOT_DECLINE_CONFIRMED;
      case '[11703-4]':
        return errorMessages.CANNOT_CONFIRM_DECLINED;
      default:
        return errorMessages.GENERIC_ERROR;
    }
  }

  if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
    return errorMessages.NETWORK_ERROR;
  }

  return errorMessages.GENERIC_ERROR;
};

export const getErrorCode = (error: any): string | null => {
  if (error?.response?.data?.kind) {
    return error.response.data.kind;
  }
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  return null;
};

export const isSuccessCase = (error: any): boolean => {
  if (error?.response?.status === 200 && error?.response?.data?.status) {
    const { status } = error.response.data;
    return status === 'confirmed' || status === 'declined';
  }

  const errorCode = getErrorCode(error);
  return errorCode === '[11703-1]' || errorCode === '[11703-2]';
};

export const getSuccessMessage = (error: any): string => {
  if (error?.response?.status === 200 && error?.response?.data?.message) {
    return error.response.data.message;
  }
  return '';
};
